import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedCheckoutData } from "../../../types/checkoutApiTypes";
import {
  TransformedAddress,
  TransformedCart,
} from "../../../../cart/types/cartApiTypes";

interface AddressSummaryProps {
  checkoutData: TransformedCheckoutData | undefined;
  cartData: TransformedCart | undefined;
  onEditBilling: () => void;
  onEditShipping: () => void;
}

const AddressSummary: React.FC<AddressSummaryProps> = ({
  checkoutData,
  cartData,
  onEditBilling,
  onEditShipping,
}) => {
  const { t } = useTranslation();

  // Find the selected shipping rate
  const selectedShippingRate = cartData?.shippingRates?.find(
    (rate) => rate.selected
  );

  // --- Access Global Data directly ---
  const allCountryStates = window.checkoutFlowVariables?.countryStates || {};
  const allowedCountries = window.checkoutFlowVariables?.allowedCountries || {};

  // Helper to render address string with Turkish specific logic
  const renderAddress = (
    address: TransformedAddress | undefined,
    type: "billing" | "shipping"
  ) => {
    if (!address) return null;

    // Determine display logic based on customer type (only relevant for Billing)
    const isCorporate = address.customerType === "corporate";
    const isIndividual = !isCorporate;
    const showCompany = type === "billing" && isCorporate;

    // --- Dynamic State Name Lookup ---
    // 1. Get states for the specific country of this address
    const statesForCountry = allCountryStates[address.country] || {};
    // 2. Lookup the name, fallback to the code if not found
    const stateName = address.state
      ? statesForCountry[address.state] || address.state
      : "";

    // --- Dynamic Country Name Lookup ---
    const countryName = allowedCountries[address.country] || address.country;

    return (
      <>
        {/* Name Line */}
        <strong>
          {address.firstName} {address.lastName}
        </strong>
        <br />
        {/* Company Line (Billing Corporate Only) */}
        {showCompany && address.company && (
          <>
            {address.company}
            <br />
          </>
        )}
        {/* TCKN / VKN Lines (Billing Only & TR Only) */}
        {type === "billing" && address.country === "TR" && (
          <>
            {isIndividual && address.tckn && (
              <>
                {t("checkoutForm.fields.tckn", "TCKN")}: {address.tckn}
                <br />
              </>
            )}
            {isCorporate && address.vkn && (
              <>
                {t("checkoutForm.fields.vkn", "VKN")}: {address.vkn}
                <br />
              </>
            )}
          </>
        )}
        {/* Standard Address Lines */}
        {address.address1}
        <br />
        {address.address2 && (
          <>
            {address.address2}
            <br />
          </>
        )}
        {address.postcode} {address.city} / {stateName}
        <br />
        {countryName}
        <br />
        {/* Contact Info */}
        {address.phone && (
          <>
            {address.phone}
            <br />
          </>
        )}
        {/* Show Email ONLY for Billing Address */}
        {type === "billing" && address.email && (
          <>
            {address.email}
            <br />
          </>
        )}
      </>
    );
  };

  return (
    <div className="order-review__grid">
      <div className="order-review__card">
        <div className="order-review__card-header">
          <h3 className="order-review__card-title">
            {t("orderReview.billingAddress", "Billing Address")}
          </h3>
          <button
            type="button"
            className="order-review__edit-btn"
            onClick={onEditBilling}
          >
            {t("orderReview.editButton", "Edit")}
          </button>
        </div>
        <div className="order-review__card-body">
          <address>
            {renderAddress(checkoutData?.billingAddress, "billing")}
          </address>
        </div>
      </div>

      {/* Shipping Address Card (Second) */}
      <div className="order-review__card">
        <div className="order-review__card-header">
          <h3 className="order-review__card-title">
            {t("orderReview.shippingAddress", "Shipping Address")}
          </h3>
          <button
            type="button"
            className="order-review__edit-btn"
            onClick={onEditShipping}
          >
            {t("orderReview.editButton", "Edit")}
          </button>
        </div>
        <div className="order-review__card-body">
          <address>
            {renderAddress(checkoutData?.shippingAddress, "shipping")}
          </address>

          {/* --- Selected Shipping Method Section --- */}
          {cartData?.needsShipping && selectedShippingRate && (
            <div className="order-review__shipping-method">
              <h4 className="order-review__sub-title">
                {t("orderReview.shippingMethod", "Shipping Method")}
              </h4>
              <div className="order-review__method-details">
                <span className="order-review__method-name">
                  {selectedShippingRate.name}
                </span>
                <span className="order-review__method-price">
                  {selectedShippingRate.price}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressSummary;
